import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Container, Typography, Paper, TextField, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Card, CardContent, Grid, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Tooltip, Divider, Tabs, Tab, Alert, Pagination, Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Timeline as TimelineIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  PowerSettingsNew as PowerIcon,
} from '@mui/icons-material';

import { ColorsConstants, borderColors, dashboardCardBgColor } from '../../../cssUtils/constants';
import { API_BASE_URL } from '../../../config/urls';

// ── axios instance ─────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: `${API_BASE_URL}/cron-jobs`,
  headers: { 'Content-Type': 'application/json' },
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const CRON_NAMES = {
  CUSTOMER: [
    'drip_marketing_cron_global',
    'refresh_media_id_cron',
    'sync_product_templates_cron',
    'audience_sync_cron',
    'manage_drip_marketing',
    'run_unpaid_invoice_cron',
    'run_invoice_cron',
    'DailyUsageChargesCronJob',
    'run_order_confirm_notification_cron',
    'run_analytics_cron '
  ],
  SYSTEM: [
    'cron_cleanup',
    'run_session_delete_cron',
    'delete_excess_kms_decryption_keys',
    'run_recurring_nodebots',
  ],
};

const buildQuery = (params) =>
  new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    )
  ).toString();

// ── Skeleton rows ──────────────────────────────────────────────────────────
const SkeletonRows = ({ cols = 7, rows = 5 }) =>
  Array.from({ length: rows }).map((_, i) => (
    <TableRow key={i}>
      {Array.from({ length: cols }).map((_, j) => (
        <TableCell key={j}>
          <Skeleton variant="text" width={j === 0 ? '80%' : '60%'} />
        </TableCell>
      ))}
    </TableRow>
  ));

// ── Enabled / Disabled badge ───────────────────────────────────────────────
const EnabledBadge = ({ enabled, schedule }) => {
  const cfg =
    enabled === true
      ? { label: 'Enabled',  color: 'success', variant: 'filled'   }
      : enabled === false
      ? { label: 'Disabled', color: 'error',   variant: 'filled'   }
      : { label: 'Unknown',  color: 'default', variant: 'outlined' };

  const chip = (
    <Chip
      size="small"
      icon={<PowerIcon sx={{ fontSize: 13 }} />}
      label={cfg.label}
      color={cfg.color}
      variant={cfg.variant}
      sx={{ fontWeight: 600 }}
    />
  );

  if (schedule) {
    return (
      <Tooltip title={`Schedule: ${schedule}`} arrow>
        <span>{chip}</span>
      </Tooltip>
    );
  }
  return chip;
};

// ── Main component ─────────────────────────────────────────────────────────
const CronMonitoringDashboard = () => {

  // ── Tabs ─────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);
  const cronType = useMemo(
    () => (activeTab === 0 ? 'SYSTEM' : 'CUSTOMER'),
    [activeTab]
  );

  // ── Dashboard filters ─────────────────────────────────────────────────────
  const [searchQuery,    setSearchQuery]    = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');
  const [cronNameFilter, setCronNameFilter] = useState('');
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd,   setDateRangeEnd]   = useState('');

  // ── Dashboard pagination ──────────────────────────────────────────────────
  const [dashboardPage,       setDashboardPage]       = useState(1);
  const [dashboardTotalPages, setDashboardTotalPages] = useState(1);
  const [dashboardCount,      setDashboardCount]      = useState(0);
  const PAGE_SIZE = 20;

  // ── Stats ─────────────────────────────────────────────────────────────────
  const [stats,        setStats]        = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError,   setStatsError]   = useState(null);

  // ── Dashboard table ───────────────────────────────────────────────────────
  const [dashboardRows,    setDashboardRows]    = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError,   setDashboardError]   = useState(null);

  // ── Filter options ────────────────────────────────────────────────────────
  // const [filterOptions, setFilterOptions] = useState({ cron_names: [] });

  // ── History modal (tenant breakdown) ─────────────────────────────────────
  const [historyOpen,      setHistoryOpen]      = useState(false);
  const [selectedCron,     setSelectedCron]     = useState(null);
  const [tenantRows,       setTenantRows]       = useState([]);
  const [tenantLoading,    setTenantLoading]    = useState(false);
  const [tenantError,      setTenantError]      = useState(null);
  const [tenantPage,       setTenantPage]       = useState(1);
  const [tenantTotalPages, setTenantTotalPages] = useState(1);
  const [tenantFilter,     setTenantFilter]     = useState('');

  const searchDebounce = useRef(null);

  // ── fetchers ───────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const { data } = await api.get(`/stats/?${buildQuery({ cron_type: cronType })}`);
      setStats(data);
    } catch {
      setStatsError('Failed to load stats.');
    } finally {
      setStatsLoading(false);
    }
  }, [cronType]);

  // const fetchFilters = useCallback(async () => {
  //   try {
  //     const { data } = await api.get(`/filters/?${buildQuery({ cron_type: cronType })}`);
  //     setFilterOptions(data);
  //   } catch { /* non-critical */ }
  // }, [cronType]);

  const fetchDashboard = useCallback(async (page = 1) => {
    setDashboardLoading(true);
    setDashboardError(null);
    try {
      const { data } = await api.get(`/dashboard/?${buildQuery({
        cron_type:  cronType,
        status:     statusFilter    || undefined,
        cron_name:  cronNameFilter  || undefined,
        search:     searchQuery     || undefined,
        start_date: dateRangeStart  || undefined,
        end_date:   dateRangeEnd    || undefined,
        page,
        page_size:  PAGE_SIZE,
      })}`);
      setDashboardRows(data.results      || []);
      setDashboardCount(data.count       || 0);
      setDashboardTotalPages(data.total_pages || 1);
      setDashboardPage(page);
    } catch {
      setDashboardError('Failed to load cron jobs.');
    } finally {
      setDashboardLoading(false);
    }
  }, [cronType, statusFilter, cronNameFilter, searchQuery, dateRangeStart, dateRangeEnd]);

  const fetchTenantBreakdown = useCallback(async (cronExecutionId, page = 1) => {
    if (!cronExecutionId) return;
    setTenantLoading(true);
    setTenantError(null);
    try {
      const { data } = await api.get(`/history/?${buildQuery({
        cron_execution_id: cronExecutionId,
        page,
        page_size: 100,
      })}`);
      setTenantRows(data.results       || []);
      setTenantTotalPages(data.total_pages || 1);
      setTenantPage(page);
    } catch {
      setTenantError('Failed to load tenant breakdown.');
    } finally {
      setTenantLoading(false);
    }
  }, []);

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    setStatusFilter('');
    setCronNameFilter('');
    setSearchQuery('');
    setDateRangeStart('');
    setDateRangeEnd('');
    setDashboardPage(1);
  }, [activeTab]);

  useEffect(() => {
    fetchStats();
    }, [fetchStats]);

  useEffect(() => {
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(
      () => fetchDashboard(1),
      searchQuery ? 400 : 0
    );
    return () => clearTimeout(searchDebounce.current);
  }, [fetchDashboard, searchQuery]);


  // Watch for filter changes and refresh dashboard
  useEffect(() => {
    // Don't fetch on initial mount
    if (dashboardPage === 1) {
      fetchDashboard(1);
    } else {
      setDashboardPage(1);
      fetchDashboard(1);
    }
  }, [statusFilter, cronNameFilter, dateRangeStart, dateRangeEnd, cronType]);

  // ── handlers ──────────────────────────────────────────────────────────────
  const handleTabChange    = (_, v) => setActiveTab(v);
  const handlePageChange   = (_, p) => fetchDashboard(p);

  const handleViewHistory  = (cron) => {
    setSelectedCron(cron);
    setTenantFilter('');
    setTenantRows([]);
    setHistoryOpen(true);
    fetchTenantBreakdown(cron.cron_execution_id);
  };

  const handleCloseHistory = () => {
    setHistoryOpen(false);
    setSelectedCron(null);
    setTenantRows([]);
    setTenantPage(1);
    setTenantFilter('');
  };

  // ── helpers ────────────────────────────────────────────────────────────────
  const getStatusColor = (s) =>
    ({ SUCCESS: 'success', RUNNING: 'info', FAILED: 'error' }[s] ?? 'default');

  const fmtDatetime = (iso) => {
    if (!iso) return '—';

    const date = new Date(iso);

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const tenantOptions = [
    ...new Map(
      tenantRows
        .filter((r) => r.tenant_id && r.tenant_name)
        .map((r) => [r.tenant_id, r.tenant_name])
    ).entries(),
  ];

  const filteredTenantRows = tenantFilter
    ? tenantRows.filter((r) => r.tenant_id === tenantFilter)
    : tenantRows;
  useEffect(() => {
    fetchDashboard(1);
  }, [statusFilter, cronNameFilter, dateRangeStart, dateRangeEnd]);

  const cronNames = CRON_NAMES[cronType] ?? [];

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">Cron Job Monitoring Dashboard</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Track cron job health and execution history across system and customer crons
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={() => { fetchStats(); fetchDashboard(dashboardPage); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab icon={<SettingsIcon />} iconPosition="start" label="System Crons"   />
            <Tab icon={<PeopleIcon   />} iconPosition="start" label="Customer Crons" />
          </Tabs>
        </Paper>

        {/* Stats cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: `${cronType === 'SYSTEM' ? 'System' : 'Customer'} Cron Jobs`, value: stats?.total_cron_jobs,     color: 'primary.main',   Icon: TimelineIcon    },
            { label: 'Successful (24h)',                                             value: stats?.successful_last_24h, color: 'success.main',   Icon: CheckCircleIcon },
            { label: 'Failed (24h)',                                                 value: stats?.failed_last_24h,     color: 'error.main',     Icon: CancelIcon      },
            { label: 'Last Execution',                                               chip:  stats?.last_execution_status, color: 'text.secondary', Icon: ScheduleIcon  },
          ].map(({ label, value, chip, color, Icon }) => (
            <Grid item xs={12} sm={6} md={3} key={label}>
              <Card sx={{ bgcolor: dashboardCardBgColor }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      {value !== undefined && (
                        <Typography variant="h4" fontWeight="bold" color={color}>
                          {statsLoading ? <Skeleton width={40} /> : (value ?? '—')}
                        </Typography>
                      )}
                      {chip !== undefined && (
                        <Box sx={{ mt: 1 }}>
                          {statsLoading
                            ? <Skeleton width={60} height={24} />
                            : chip
                              ? <Chip label={chip} size="small" color={getStatusColor(chip)} />
                              : <Typography variant="body2" color="text.secondary">—</Typography>
                          }
                        </Box>
                      )}
                    </Box>
                    <Icon sx={{ fontSize: 40, color, opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Main table */}
        <Paper>
          <Box sx={{ p: 2, bgcolor: dashboardCardBgColor, borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}` }}>
            <Typography variant="h6" fontWeight="bold">
              {cronType === 'SYSTEM' ? '⚙️ System Cron Jobs' : '👥 Customer Cron Jobs'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              One row per execution — click the eye icon to see tenant breakdown
            </Typography>
          </Box>

          {/* Filters */}
          <Box sx={{ p: 2, bgcolor: dashboardCardBgColor, borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}` }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth size="small" placeholder="Search cron jobs…"
                  value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
                />
              </Grid>
              <Grid item xs={12} md={2.5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select 
                    value={statusFilter} 
                    label="Status" 
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >

                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="SUCCESS">Success</MenuItem>
                    <MenuItem value="RUNNING">Running</MenuItem>
                    <MenuItem value="FAILED">Failed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cron Name</InputLabel>
                  <Select 
                    value={cronNameFilter} 
                    label="Cron Name" 
                    onChange={(e) => setCronNameFilter(e.target.value)}
                  >
                    <MenuItem value="">All Cron Jobs</MenuItem>
                    {cronNames.map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1.75}>
                <TextField fullWidth size="small" label="Start Date" type="date"
                  value={dateRangeStart} onChange={(e) => setDateRangeStart(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} md={1.75}>
                <TextField fullWidth size="small" label="End Date" type="date"
                  value={dateRangeEnd} onChange={(e) => setDateRangeEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Showing {dashboardRows.length} of {dashboardCount} executions
            </Typography>
          </Box>

          {dashboardError && <Alert severity="error" sx={{ m: 2 }}>{dashboardError}</Alert>}

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Cron Name</strong></TableCell>
                  <TableCell><strong>Start Time</strong></TableCell>
                  <TableCell><strong>End Time</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Duration</strong></TableCell>
                  <TableCell><strong>Tenants</strong></TableCell>
                  <TableCell><strong>Records</strong></TableCell>
                  <TableCell><strong>Scheduler</strong></TableCell>
                  <TableCell align="center"><strong>Breakdown</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardLoading
                  ? <SkeletonRows cols={9} rows={6} />
                  : dashboardRows.map((cron) => (
                    <TableRow key={cron.cron_execution_id} hover>

                      {/* Cron name */}
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{cron.cron_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{cron.environment}</Typography>
                      </TableCell>

                      {/* Start time */}
                      <TableCell>
                        <Typography variant="body2">{fmtDatetime(cron.start_time)}</Typography>
                      </TableCell>

                      {/* End time */}
                      <TableCell>
                        <Typography variant="body2">{fmtDatetime(cron.end_time)}</Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip label={cron.status} size="small" color={getStatusColor(cron.status)} />
                        {cron.error_message && (
                          <Tooltip title={cron.error_message} arrow>
                            <Typography variant="caption" color="error"
                              sx={{ maxWidth: 150, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {cron.error_message}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>

                      {/* Duration */}
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {cron.duration_display || '—'}
                        </Typography>
                      </TableCell>

                      {/* Total tenants */}
                      <TableCell>
                        {cron.total_tenants > 0
                          ? <Chip label={cron.total_tenants} size="small" variant="outlined" color="primary" />
                          : <Typography variant="body2" color="text.secondary">—</Typography>
                        }
                      </TableCell>

                      {/* Records processed */}
                      <TableCell>
                        {cron.total_records != null
                          ? <Chip label={cron.total_records.toLocaleString()} size="small" color="success" variant="outlined" />
                          : <Typography variant="body2" color="text.secondary">—</Typography>}
                      </TableCell>

                      {/* Scheduler */}
                      <TableCell>
                        <EnabledBadge enabled={cron.enabled} schedule={cron.schedule} />
                      </TableCell>

                      {/* Breakdown — only show for customer crons with tenants */}
                      <TableCell align="center">
                        {cron.cron_type === 'CUSTOMER' && cron.total_tenants > 0
                          ? (
                            <Tooltip title="View Tenant Breakdown">
                              <IconButton size="small" color="primary" onClick={() => handleViewHistory(cron)}>
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          )
                          : <Typography variant="body2" color="text.secondary">—</Typography>
                        }
                      </TableCell>

                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>

          {!dashboardLoading && dashboardRows.length === 0 && !dashboardError && (
            <Box sx={{ p: 8, textAlign: 'center' }}>
              <ErrorIcon sx={{ fontSize: 48, color: ColorsConstants.primaryGray30, mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No cron jobs found</Typography>
              <Typography variant="body2" color="text.secondary">Try adjusting your filters</Typography>
            </Box>
          )}

          {/* Pagination */}
          {dashboardTotalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Pagination
                count={dashboardTotalPages}
                page={dashboardPage}
                onChange={handlePageChange}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </Paper>

        {/* ── Tenant Breakdown Modal ─────────────────────────────────────── */}
        <Dialog open={historyOpen} onClose={handleCloseHistory} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6">{selectedCron?.cron_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Tenant breakdown · {fmtDatetime(selectedCron?.start_time)}
                </Typography>
              </Box>
              <IconButton onClick={handleCloseHistory} size="small"><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <Divider />

          <DialogContent>
            {tenantError && <Alert severity="error" sx={{ mb: 2 }}>{tenantError}</Alert>}

            {/* Tenant filter dropdown */}
            {!tenantLoading && tenantRows.length > 0 && (
              <Box sx={{ mb: 2, maxWidth: 300 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Filter by Tenant</InputLabel>
                  <Select
                    value={tenantFilter}
                    label="Filter by Tenant"
                    onChange={(e) => setTenantFilter(e.target.value)}
                  >
                    <MenuItem value="">All Tenants</MenuItem>
                    {tenantOptions.map(([id, name]) => (
                      <MenuItem key={id} value={id}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {tenantLoading
              ? <Table size="small"><TableBody><SkeletonRows cols={7} rows={5} /></TableBody></Table>
              : filteredTenantRows.length === 0
              ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No tenant logs for this execution.</Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Showing {filteredTenantRows.length} of {tenantRows.length} tenants
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Tenant</strong></TableCell>
                          <TableCell><strong>Start Time</strong></TableCell>
                          <TableCell><strong>End Time</strong></TableCell>
                          <TableCell><strong>Status</strong></TableCell>
                          <TableCell><strong>Duration</strong></TableCell>
                          <TableCell><strong>Records</strong></TableCell>
                          <TableCell><strong>Error</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTenantRows.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {row.tenant_name || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>{fmtDatetime(row.start_time)}</TableCell>
                            <TableCell>{fmtDatetime(row.end_time)}</TableCell>
                            <TableCell>
                              <Chip label={row.status} size="small" color={getStatusColor(row.status)} />
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {row.duration_display || '—'}
                            </TableCell>
                            <TableCell>
                              {row.records_processed != null
                                ? <Chip label={row.records_processed} size="small"
                                    color={row.records_processed > 0 ? 'success' : 'default'} variant="outlined" />
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {row.error_message
                                ? (
                                  <Tooltip title={row.error_message} arrow>
                                    <Typography variant="caption" color="error"
                                      sx={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {row.error_message}
                                    </Typography>
                                  </Tooltip>
                                )
                                : <Typography variant="caption" color="text.secondary">—</Typography>
                              }
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {tenantTotalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={tenantTotalPages}
                        page={tenantPage}
                        onChange={(_, p) => fetchTenantBreakdown(selectedCron.cron_execution_id, p)}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  )}
                </>
              )
            }
          </DialogContent>

          <Divider />
          <DialogActions>
            <Button onClick={handleCloseHistory} variant="outlined">Close</Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
};

export default CronMonitoringDashboard;